import { useTranslations } from '@vocab/react';
import { Alert, Stack, Strong, Text, TextLink } from 'braid-design-system';

import translations from './.vocab';

import { useQueryAccountInfo } from 'src/App/hooks/useQueryAccountInfo';
import { useMelwaysLink } from '@seek/melways-react';
import { URL_ACCOUNT_INVOICING_PAYABLE } from '@seek/adv-constants';
import { useAppContext } from 'src/App/context/AppContext';
import type { Language } from '@seek/melways-sites';
import { useTracking } from 'src/App/context/TrackingProvider';
import { useEffect } from 'react';

interface Props {
  renderAlert?: boolean;
}

export const CreditHoldError = ({ renderAlert = true }: Props) =>
  renderAlert ? (
    <Alert tone="critical">
      <CreditHoldErrorBody />
    </Alert>
  ) : (
    <CreditHoldErrorBody />
  );

export const CreditHoldErrorBody = () => {
  const { language } = useAppContext();
  const { t } = useTranslations(translations);
  const { trackLink } = useTracking();
  const { accountInfo } = useQueryAccountInfo();
  const userCanPayInvoices =
    accountInfo?.advertiser?.permissionOptions.canPayInvoices;
  const localize = useMelwaysLink();

  useEffect(() => {
    trackLink({
      eventName: 'CREDIT_HOLD_OUTSTANDING_INVOICES_VIEWED',
      data: { userCanPayInvoices },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInvoicingPayablesURlClicked = () =>
    trackLink({
      eventName: 'CREDIT_HOLD_OUTSTANDING_INVOICES_PRESSED',
      data: { objectInteraction: 'credit-hold-outstanding-invoices' },
    });

  return (
    <Stack space="medium">
      <Text>
        <Strong>{t('your account is on credit hold')}</Strong>
      </Text>
      <Text>
        {!userCanPayInvoices ? (
          <>{t('your administrator needs to pay overdue invoices...')}</>
        ) : (
          <>
            {t('you need to pay overdue invoices...', {
              Link: (children) => (
                <TextLink
                  href={localize({
                    path: URL_ACCOUNT_INVOICING_PAYABLE,
                    language: language as Language,
                  })}
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={onInvoicingPayablesURlClicked}
                >
                  {children}
                </TextLink>
              ),
            })}
          </>
        )}
      </Text>
    </Stack>
  );
};
